import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Flame, Clock, Calendar, Info, Loader2, CreditCard, FileText, ExternalLink, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { PayPalCheckout } from '../components/PayPalCheckout';
import TransferPaymentBlock from '../components/TransferPaymentBlock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShabbatPage = () => {
  const { t, language } = useLanguage();
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingMeal, setPayingMeal] = useState(null);
  const [currency, setCurrency] = useState('MXN');
  const [mealForm, setMealForm] = useState({ guest_name: '', guest_phone: '', guest_email: '', guests_count: 1, notes: '', phoneCode: '+52' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSent, setRegistrationSent] = useState(false);

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const response = await axios.get(`${API}/shabbat/schedule`);
        setSchedule(response.data);
      } catch (error) {
        console.error('Error fetching schedule:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSchedule();
  }, []);

  const getInstructions = () => {
    if (!schedule) return '';
    return schedule[`instructions_${language}`] || schedule.instructions_es;
  };

  const formatPrice = (price, priceUsd) => {
    if (currency === 'USD' && priceUsd) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(priceUsd);
    }
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(price);
  };

  const validateMealForm = () => {
    if (!mealForm.guest_name || !mealForm.guest_phone || !mealForm.guest_email) {
      toast.error('Completa nombre, email y telefono');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mealForm.guest_email.trim())) {
      toast.error('Ingresa un email válido (para enviarte la confirmación y aprobación)');
      return false;
    }
    return true;
  };

  // Calcula el total para PayPal en la moneda seleccionada
  const getMealTotal = () => {
    if (!payingMeal || payingMeal === 'prayers_only') return 0;
    const useUsd = currency === 'USD';
    const dinner = useUsd ? (schedule?.friday_dinner_cost_usd || 15) : (schedule?.friday_dinner_cost || 250);
    const lunch = useUsd ? (schedule?.saturday_lunch_cost_usd || 12) : (schedule?.saturday_lunch_cost || 200);
    let unit = 0;
    if (payingMeal === 'friday_dinner') unit = dinner;
    else if (payingMeal === 'saturday_lunch') unit = lunch;
    else if (payingMeal === 'both') unit = dinner + lunch;
    return Math.round(unit * mealForm.guests_count * 100) / 100;
  };

  const handleRegisterOnly = async () => {
    if (!mealForm.guest_name || !mealForm.guest_phone || !mealForm.guest_email) {
      toast.error('Completa nombre, email y telefono');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/shabbat/register`, {
        name: mealForm.guest_name,
        email: mealForm.guest_email,
        phone: `${mealForm.phoneCode} ${mealForm.guest_phone}`,
        guests_count: mealForm.guests_count,
        meal_type: 'prayers_only',
        has_paid: false,
        notes: mealForm.notes || null,
        language: language
      });
      setPayingMeal(null);
      setRegistrationSent(true);
      toast.success(language === 'es' ? 'Solicitud enviada! Recibiras confirmacion por email.' : 'Request sent! You will receive confirmation by email.');
    } catch {
      toast.error('Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const labels = {
    es: { arvit: 'Arvit', shacharit: 'Shajarit', mincha: 'Minjá', fridayDinner: 'Comida de noche de Shabbat', saturdayLunch: 'Comida de día de Shabbat', payMeal: 'Pagar y registrar', payBoth: 'Pagar ambas comidas', guests: 'Invitados', name: 'Nombre *', phone: 'Telefono *', email: 'Email * (para confirmacion)', pay: 'Pagar ahora', formBtn: 'Formulario para asistir a la comunidad', perPerson: 'por persona', reminderForm: 'Recuerda: debes llenar el formulario de asistencia. Te enviaremos confirmacion por email.', registerOnly: 'Solo asistir a rezos (sin comida)', paypalNote: 'PayPal permite pagar con tarjeta como invitado sin cuenta', notes: 'Notas (opcional)' },
    en: { arvit: 'Arvit', shacharit: 'Shacharit', mincha: 'Mincha', fridayDinner: 'Shabbat Night Meal', saturdayLunch: 'Shabbat Day Meal', payMeal: 'Pay & Register', payBoth: 'Pay for both meals', guests: 'Guests', name: 'Name *', phone: 'Phone *', email: 'Email * (for confirmation)', pay: 'Pay now', formBtn: 'Form to attend the community', perPerson: 'per person', reminderForm: 'Remember: you must fill out the attendance form. We will send you confirmation by email.', registerOnly: 'Attend prayers only (no meal)', paypalNote: 'PayPal allows paying with card as guest without an account', notes: 'Notes (optional)' },
    he: { arvit: 'ערבית', shacharit: 'שחרית', mincha: 'מנחה', fridayDinner: 'סעודת ליל שבת', saturdayLunch: 'סעודת יום שבת', payMeal: 'שלם והירשם', payBoth: 'שלם עבור שתי הארוחות', guests: 'אורחים', name: 'שם *', phone: 'טלפון *', email: 'אימייל * (לאישור)', pay: 'שלם עכשיו', formBtn: 'טופס להשתתפות בקהילה', perPerson: 'לאדם', reminderForm: 'זכור: עליך למלא את טופס ההשתתפות. נשלח לך אישור באימייל.', registerOnly: 'השתתף בתפילות בלבד (ללא ארוחה)', paypalNote: 'PayPal מאפשר תשלום בכרטיס כאורח', notes: 'הערות (אופציונלי)' }
  };
  const l = labels[language] || labels.es;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-20 pb-12">
      {/* Hero */}
      <section className="relative h-[300px] overflow-hidden mb-8">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_jabad-gdl/artifacts/nr8klcj9_Image%203.jpeg)' }}>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F2922]/70 to-[#0F2922]/50" />
        </div>
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-2" data-testid="shabbat-title">{t('shabbatTitle')}</h1>
            <p className="font-body text-white/80 text-lg">{t('shabbatSchedule')}</p>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Candle Lighting */}
          <Card className="bg-white border-[#E5E3D8] rounded-2xl" data-testid="candle-times-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-xl text-[#0F2922] flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37] flex items-center justify-center"><Flame className="w-5 h-5 text-white" /></div>
                {t('candleLighting')} & {t('havdalah')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-[#FAF9F6] rounded-xl">
                <span className="font-body text-[#5C6C62]">{t('candleLighting')}</span>
                <span className="font-heading font-bold text-[#0F2922] text-lg">{schedule?.candle_lighting || '18:30'}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#FAF9F6] rounded-xl">
                <span className="font-body text-[#5C6C62]">{t('havdalah')}</span>
                <span className="font-heading font-bold text-[#0F2922] text-lg">{schedule?.havdalah || '19:30'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Prayer Times */}
          <Card className="bg-white border-[#E5E3D8] rounded-2xl" data-testid="prayer-times-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-xl text-[#0F2922] flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#4A5D52] flex items-center justify-center"><Clock className="w-5 h-5 text-white" /></div>
                {language === 'es' ? 'Rezos de Shabbat' : language === 'en' ? 'Shabbat Prayers' : 'תפילות שבת'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-[#FAF9F6] rounded-xl">
                <span className="font-body text-[#5C6C62]">{l.arvit}</span>
                <span className="font-heading font-bold text-[#0F2922] text-lg">{schedule?.arvit || '19:00'}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#FAF9F6] rounded-xl">
                <span className="font-body text-[#5C6C62]">{l.shacharit}</span>
                <span className="font-heading font-bold text-[#0F2922] text-lg">{schedule?.shacharit || '09:00'}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#FAF9F6] rounded-xl">
                <span className="font-body text-[#5C6C62]">{l.mincha}</span>
                <span className="font-heading font-bold text-[#0F2922] text-lg">{schedule?.mincha || '18:00'}</span>
              </div>
              <div className="p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl">
                <p className="font-body text-sm text-[#0F2922]">
                  {language === 'es' ? 'Para asistir a los rezos es necesario llenar el formulario que se encuentra abajo.' : language === 'en' ? 'To attend prayers you must fill out the form below.' : 'כדי להשתתף בתפילות יש למלא את הטופס למטה.'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Meals */}
          <Card className="bg-white border-[#E5E3D8] rounded-2xl md:col-span-2" data-testid="meals-card">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl text-[#0F2922] flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#B85042] flex items-center justify-center"><Calendar className="w-5 h-5 text-white" /></div>
                  {t('meals')}
                </CardTitle>
                <div className="flex items-center bg-[#FAF9F6] border border-[#E5E3D8] rounded-xl overflow-hidden">
                  <button onClick={() => setCurrency('MXN')} className={`px-4 py-2 text-xs font-bold transition-colors ${currency === 'MXN' ? 'bg-[#0F2922] text-white' : 'text-[#5C6C62]'}`} data-testid="shabbat-currency-mxn">MXN</button>
                  <button onClick={() => setCurrency('USD')} className={`px-4 py-2 text-xs font-bold transition-colors ${currency === 'USD' ? 'bg-[#0F2922] text-white' : 'text-[#5C6C62]'}`} data-testid="shabbat-currency-usd">USD</button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-5 bg-[#FAF9F6] rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-heading font-bold text-[#0F2922] text-lg">{l.fridayDinner}</span>
                    <span className="font-heading font-bold text-[#B85042] text-lg">
                      {currency === 'USD' ? `$${(schedule?.friday_dinner_cost_usd || 15).toFixed(2)} USD` : `$${(schedule?.friday_dinner_cost || 250).toFixed(2)} MXN`}
                      <span className="text-xs font-normal text-[#5C6C62] ml-1">{l.perPerson}</span>
                    </span>
                  </div>
                  <Button className="w-full bg-[#B85042] hover:bg-[#B85042]/90 text-white rounded-xl" onClick={() => setPayingMeal('friday_dinner')} data-testid="pay-friday-dinner">
                    <CreditCard className="w-4 h-4 mr-2" />{l.payMeal}
                  </Button>
                </div>
                <div className="p-5 bg-[#FAF9F6] rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-heading font-bold text-[#0F2922] text-lg">{l.saturdayLunch}</span>
                    <span className="font-heading font-bold text-[#B85042] text-lg">
                      {currency === 'USD' ? `$${(schedule?.saturday_lunch_cost_usd || 12).toFixed(2)} USD` : `$${(schedule?.saturday_lunch_cost || 200).toFixed(2)} MXN`}
                      <span className="text-xs font-normal text-[#5C6C62] ml-1">{l.perPerson}</span>
                    </span>
                  </div>
                  <Button className="w-full bg-[#B85042] hover:bg-[#B85042]/90 text-white rounded-xl" onClick={() => setPayingMeal('saturday_lunch')} data-testid="pay-saturday-lunch">
                    <CreditCard className="w-4 h-4 mr-2" />{l.payMeal}
                  </Button>
                </div>
              </div>
              {/* Pay Both + Register Only */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button className="bg-[#0F2922] hover:bg-[#0F2922]/90 text-white rounded-xl py-5" onClick={() => setPayingMeal('both')} data-testid="pay-both-meals">
                  <CreditCard className="w-5 h-5 mr-2" />{l.payBoth}
                </Button>
                <Button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white rounded-xl py-5" onClick={() => setPayingMeal('prayers_only')} data-testid="register-prayers-only">
                  {l.registerOnly}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="bg-white border-[#E5E3D8] rounded-2xl md:col-span-2" data-testid="instructions-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-xl text-[#0F2922] flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#0F2922] flex items-center justify-center"><Info className="w-5 h-5 text-white" /></div>
                {t('instructions')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-body text-[#5C6C62] leading-relaxed">{getInstructions()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Payment/Registration Modal */}
        {payingMeal && (
          <div className="fixed inset-0 bg-black/50 z-[60] overflow-y-auto" onClick={(e) => e.target === e.currentTarget && setPayingMeal(null)}>
            <div className="min-h-full flex items-start justify-center py-8 px-4">
              <Card className="w-full max-w-md bg-white border-[#E5E3D8] rounded-2xl" data-testid="meal-payment-form">
                <CardHeader>
                  <CardTitle className="font-heading text-[#0F2922]">
                    {payingMeal === 'prayers_only' ? l.registerOnly : payingMeal === 'both' ? `${l.fridayDinner} + ${l.saturdayLunch}` : payingMeal === 'friday_dinner' ? l.fridayDinner : l.saturdayLunch}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Reminder */}
                  <div className="p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl">
                    <p className="text-sm text-[#0F2922] font-body">{l.reminderForm}</p>
                  </div>

                  <div>
                    <Label className="text-[#0F2922] font-body">{l.name}</Label>
                    <Input value={mealForm.guest_name} onChange={(e) => setMealForm(p => ({ ...p, guest_name: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" data-testid="meal-name" />
                  </div>
                  <div>
                    <Label className="text-[#0F2922] font-body">{l.email}</Label>
                    <Input type="email" value={mealForm.guest_email} onChange={(e) => setMealForm(p => ({ ...p, guest_email: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" data-testid="meal-email" />
                  </div>
                  <div>
                    <Label className="text-[#0F2922] font-body">{l.phone}</Label>
                    <div className="flex gap-2 mt-1">
                      <select
                        value={mealForm.phoneCode || '+52'}
                        onChange={(e) => setMealForm(p => ({ ...p, phoneCode: e.target.value }))}
                        className="w-[100px] h-10 px-2 border border-[#E5E3D8] rounded-xl bg-white text-sm text-[#0F2922] font-body"
                        data-testid="meal-phone-code"
                      >
                        <option value="+52">+52 MX</option>
                        <option value="+1">+1 US/CA</option>
                        <option value="+972">+972 IL</option>
                        <option value="+44">+44 UK</option>
                        <option value="+33">+33 FR</option>
                        <option value="+49">+49 DE</option>
                        <option value="+55">+55 BR</option>
                        <option value="+54">+54 AR</option>
                        <option value="+57">+57 CO</option>
                        <option value="+34">+34 ES</option>
                      </select>
                      <Input value={mealForm.guest_phone} onChange={(e) => setMealForm(p => ({ ...p, guest_phone: e.target.value }))} className="flex-1 border-[#E5E3D8] rounded-xl" placeholder="333 146 3068" data-testid="meal-phone" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#0F2922] font-body">{l.guests}</Label>
                    <Input type="number" min="1" value={mealForm.guests_count} onChange={(e) => setMealForm(p => ({ ...p, guests_count: parseInt(e.target.value) || 1 }))} className="mt-1 border-[#E5E3D8] rounded-xl" data-testid="meal-guests" />
                  </div>
                  <div>
                    <Label className="text-[#0F2922] font-body">{l.notes}</Label>
                    <Textarea value={mealForm.notes} onChange={(e) => setMealForm(p => ({ ...p, notes: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" rows={2} />
                  </div>

                  {/* Total del pago en la moneda seleccionada */}
                  {payingMeal !== 'prayers_only' && (
                    <div className="p-4 bg-[#FAF9F6] rounded-xl text-center">
                      <p className="font-body text-sm text-[#5C6C62]">{t('total')}</p>
                      <p className="font-heading text-2xl font-bold text-[#B85042]">
                        ${getMealTotal().toFixed(2)} {currency}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col gap-3">
                    {payingMeal === 'prayers_only' ? (
                      <Button className="w-full bg-[#0F2922] hover:bg-[#0F2922]/90 text-white rounded-xl" onClick={handleRegisterOnly} disabled={isSubmitting} data-testid="submit-register-only">
                        {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        {l.registerOnly}
                      </Button>
                    ) : (
                      <div data-testid="meal-paypal-wrapper">
                        <p className="text-xs text-[#5C6C62] mb-2 text-center">
                          {language === 'es' ? 'Pago seguro con PayPal o tarjeta (sin cuenta).' : language === 'en' ? 'Secure payment with PayPal or card (no account).' : 'תשלום מאובטח עם PayPal או כרטיס (ללא חשבון).'}
                        </p>
                        <PayPalCheckout
                          amount={getMealTotal()}
                          currency={currency}
                          referenceType="shabbat"
                          description={`Jabad GDL - ${payingMeal === 'both' ? 'Ambas comidas' : payingMeal === 'friday_dinner' ? 'Comida de noche de Shabbat' : 'Comida de día de Shabbat'} x${mealForm.guests_count} - ${mealForm.guest_name}`}
                          payload={{
                            guest_name: mealForm.guest_name,
                            guest_email: mealForm.guest_email,
                            guest_phone: `${mealForm.phoneCode} ${mealForm.guest_phone}`,
                            guests_count: mealForm.guests_count,
                            meal_type: payingMeal,
                            notes: mealForm.notes || null,
                            language: language,
                          }}
                          validate={validateMealForm}
                          onSuccess={() => {
                            setPayingMeal(null);
                            setRegistrationSent(true);
                          }}
                        />
                        <TransferPaymentBlock
                          amount={getMealTotal()}
                          currency={currency}
                          validate={validateMealForm}
                          beforeCreate={async () => {
                            // Register the guest as unpaid; admin marks paid later.
                            const res = await axios.post(`${API}/shabbat/register`, {
                              name: mealForm.guest_name,
                              email: mealForm.guest_email,
                              phone: `${mealForm.phoneCode} ${mealForm.guest_phone}`,
                              guests_count: mealForm.guests_count,
                              meal_type: payingMeal,
                              has_paid: false,
                              notes: mealForm.notes || null,
                              language: language,
                            });
                            return { referenceId: res.data.id, amount: getMealTotal() };
                          }}
                          onConfirmed={() => {
                            setPayingMeal(null);
                            setRegistrationSent(true);
                          }}
                        />
                      </div>
                    )}
                    <Button variant="ghost" className="w-full text-sm text-[#5C6C62]" onClick={() => setPayingMeal(null)}>{t('cancel')}</Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Registration Success */}
        {registrationSent && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setRegistrationSent(false)}>
            <Card className="w-full max-w-sm bg-white border-[#E5E3D8] rounded-2xl">
              <CardContent className="p-8 text-center">
                <CheckCircle className="w-16 h-16 text-[#25D366] mx-auto mb-4" />
                <h2 className="font-heading text-xl font-bold text-[#0F2922] mb-2">{language === 'es' ? 'Solicitud enviada' : 'Request sent'}</h2>
                <p className="font-body text-[#5C6C62]">{language === 'es' ? 'Recibirás confirmación por email' : 'You will receive confirmation by email'}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href="https://forms.gle/PXaaZkyWJNgfcezb7" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-8 py-4 bg-[#0F2922] text-white rounded-xl font-body font-medium hover:bg-[#0F2922]/90 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid="community-form-button">
            <FileText className="w-6 h-6" />
            {l.formBtn}
            <ExternalLink className="w-4 h-4" />
          </a>
          <a href="https://wa.me/523331463068?text=Hola,%20me%20gustar%C3%ADa%20reservar%20para%20las%20comidas%20de%20Shabbat" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-8 py-4 bg-[#25D366] text-white rounded-xl font-body font-medium hover:bg-[#25D366]/90 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid="reserve-whatsapp">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            {language === 'es' ? 'Reservar por WhatsApp' : language === 'en' ? 'Reserve via WhatsApp' : 'הזמן בוואטסאפ'}
          </a>
        </div>
      </div>
    </div>
  );
};

export default ShabbatPage;
